// Offer Response Page - Public page for customers to respond to offers
import { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { API_URL } from "../config/api";
import {
  CheckCircle,
  XCircle,
  RefreshCw,
  Loader2,
  Package,
  MapPin,
  Calendar,
  DollarSign,
  Ship,
  Plane,
  Truck
} from "lucide-react";


export default function OfferResponsePage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [offerData, setOfferData] = useState(null);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submittedAction, setSubmittedAction] = useState(null);
  
  // Check for action in URL params
  const actionFromUrl = searchParams.get("action");

  useEffect(() => {
    fetchOfferData();
  }, [token]);

  // Auto-submit if action is in URL
  useEffect(() => {
    if (offerData && actionFromUrl && !submitted) {
      handleResponse(actionFromUrl);
    }
  }, [offerData, actionFromUrl]);

  const fetchOfferData = async () => {
    try {
      const response = await axios.get(`${API_URL}/public/offer-response/${token}`);
      setOfferData(response.data);
    } catch (err) {
      setError("This offer link is invalid or has expired.");
    } finally {
      setLoading(false);
    }
  };

  const handleResponse = async (action) => {
    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/public/offer-response/${token}?action=${action}`);
      setSubmitted(true);
      setSubmittedAction(action);
      
      if (action === "reject" && response.data.redirect) {
        // Redirect to feedback page
        setTimeout(() => {
          navigate(`/offer-feedback/${token}`);
        }, 2000);
      }
      
      toast.success(response.data.message);
    } catch (err) {
      toast.error("Failed to submit response. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const getServiceIcon = (serviceType) => {
    if (!serviceType) return Package;
    const type = serviceType.toLowerCase();
    if (type.includes("air")) return Plane;
    if (type.includes("fcl") || type.includes("lcl") || type.includes("ocean")) return Ship;
    if (type.includes("truck") || type.includes("inland")) return Truck;
    return Package;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Link Expired</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center"
        >
          {submittedAction === "confirm" && (
            <>
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h1>
              <p className="text-gray-600">Your confirmation has been received. We'll be in touch shortly to proceed with your shipment.</p>
            </>
          )}
          {submittedAction === "reject" && (
            <>
              <XCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Response Received</h1>
              <p className="text-gray-600">We understand. Redirecting you to share your feedback...</p>
            </>
          )}
          {submittedAction === "update" && (
            <>
              <RefreshCw className="h-16 w-16 text-blue-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Update Requested</h1>
              <p className="text-gray-600">We'll prepare an updated quote for you and send it to your email shortly.</p>
            </>
          )}
        </motion.div>
      </div>
    );
  }

  const { offer_summary, followup } = offerData || {};
  const ServiceIcon = getServiceIcon(offer_summary?.service_type);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 py-12 px-4">
      {/* Header */}
      <div className="max-w-2xl mx-auto mb-8 text-center">
        <div className="inline-flex items-center gap-2 bg-[#1e3a5f] text-white px-6 py-3 rounded-full mb-6">
          <Ship className="h-5 w-5" />
          <span className="font-semibold">Europacific</span>
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Your Quotation</h1>
        <p className="text-gray-600">Please review and respond to your quote</p>
      </div>

      {/* Offer Summary Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden mb-8"
      >
        <div className="bg-[#1e3a5f] p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-200">Quote Number</p>
              <p className="text-2xl font-bold">{offer_summary?.offer_number}</p>
            </div>
            <ServiceIcon className="h-12 w-12 text-blue-200" />
          </div>
        </div>

        <div className="p-6 space-y-4">
          {offer_summary?.origin && offer_summary?.destination && (
            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
              <MapPin className="h-5 w-5 text-primary" />
              <div className="flex-1">
                <p className="text-sm text-gray-500">Route</p>
                <p className="font-semibold text-gray-900">
                  {offer_summary.origin} → {offer_summary.destination}
                </p>
              </div>
            </div>
          )}

          {offer_summary?.subtotal && (
            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
              <DollarSign className="h-5 w-5 text-green-600" />
              <div className="flex-1">
                <p className="text-sm text-gray-500">Total Amount</p>
                <p className="font-semibold text-gray-900 text-xl">
                  {offer_summary.currency} {offer_summary.subtotal?.toLocaleString()}
                </p>
              </div>
            </div>
          )}

          <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
            <Calendar className="h-5 w-5 text-amber-600" />
            <div className="flex-1">
              <p className="text-sm text-gray-500">Valid Until</p>
              <p className="font-semibold text-gray-900">
                {offer_summary?.validity_end ? new Date(offer_summary.validity_end).toLocaleDateString() : "N/A"}
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Action Buttons */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="max-w-2xl mx-auto"
      >
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 text-center">
            What would you like to do?
          </h2>
          
          <div className="grid gap-4">
            <Button
              onClick={() => handleResponse("confirm")}
              disabled={submitting}
              className="w-full h-14 bg-green-600 hover:bg-green-700 text-white text-lg"
              data-testid="confirm-offer-btn"
            >
              {submitting ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  <CheckCircle className="h-5 w-5 mr-2" />
                  Confirm Offer
                </>
              )}
            </Button>
            
            <Button
              onClick={() => handleResponse("update")}
              disabled={submitting}
              variant="outline"
              className="w-full h-14 border-amber-500 text-amber-600 hover:bg-amber-50 text-lg"
              data-testid="request-update-btn"
            >
              <RefreshCw className="h-5 w-5 mr-2" />
              Request Updated Quote
            </Button>
            
            <Button
              onClick={() => handleResponse("reject")}
              disabled={submitting}
              variant="outline"
              className="w-full h-14 border-gray-300 text-gray-600 hover:bg-gray-50 text-lg"
              data-testid="decline-offer-btn"
            >
              <XCircle className="h-5 w-5 mr-2" />
              Decline Offer
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Footer */}
      <div className="max-w-2xl mx-auto mt-8 text-center text-gray-500 text-sm">
        <p>Questions? Contact us at info@europacific.si</p>
        <p className="mt-2">© 2026 Europacific d.o.o. | Logistics Simplified</p>
      </div>
    </div>
  );
}
